import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, getMe } from '../services/ownerApi.js';
import { getLoginErrorMessage } from '../utils/loginErrorMessages.js';

/**
 * useLogin - Login state and submission logic hook
 * 
 * Manages login form state (email, password, error, loading) and handles the complete
 * login flow: credential validation, session verification, password reset redirects,
 * and success callbacks. Preserves exact sequencing and correlation ID behavior.
 * 
 * @param {Function} onLoginSuccess - Callback invoked with user data after successful login
 * @returns {Object} Login state and handlers
 */
export function useLogin(onLoginSuccess) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [siteAccessDenied, setSiteAccessDenied] = useState(null);
  
  // AbortController ref to track in-flight requests and cancel on unmount or new submit
  const abortControllerRef = useRef(null);
  // Mounted ref to prevent setState after unmount
  const isMountedRef = useRef(true);
  
  // Cleanup on unmount: abort any in-flight requests
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);
  
  // Helper to check if we should set state (component still mounted)
  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };
  
  // Helper to check if debug logging is enabled
  const shouldLog = () => {
    if (typeof window === 'undefined') return false;
    return process.env.NODE_ENV === 'development' || 
           new URLSearchParams(window.location.search).get('debug_login') === '1';
  };

  /**
   * Handles form submission, authenticates user, and navigates on success.
   * 
   * Intent: Orchestrates the complete login flow: validates credentials via backend API,
   * verifies session cookie was set by calling /me endpoint, handles password reset redirects,
   * and invokes parent callback with user data before navigating to calendar page. Includes
   * error handling for various failure scenarios (invalid credentials, role restrictions,
   * missing owner link, inactive accounts, session establishment failures).
   * 
   * Workflow (preserved verbatim):
   * 1. Prevent default form submission and clear previous errors
   * 2. Call login API with email/password credentials
   * 3. If login fails, display error message and stop
   * 4. If mustResetPassword is true, redirect to password reset page
   * 5. Otherwise, call /me endpoint to verify session cookie was set
   * 6. If /me fails, display error (session not established)
   * 7. If /me succeeds, call onLoginSuccess callback and navigate to /calendar
   * 
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this submission
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;
    
    safeSetState(setError, null);
    safeSetState(setSiteAccessDenied, null);
    safeSetState(setLoading, true);

    const cid = Math.random().toString(36).slice(2, 10);
    if (shouldLog()) {
      console.log('[LOGIN] submit clicked', { cid, email });
    }

    try {
      const response = await login(email, password, signal);
      
      // Check if request was aborted
      if (signal.aborted) {
        return; // Don't set state if aborted
      }
      
      if (shouldLog()) {
        console.log('[LOGIN] login response status/outcome', { cid, status: 200, outcome: response.outcome });
      }

      if (response.outcome !== 'success') {
        safeSetState(setError, response.message || 'Login failed');
        safeSetState(setLoading, false);
        return;
      }

      if (response.data?.mustResetPassword === true) {
        navigate('/owner/reset-password', { replace: true });
        return;
      }

      // Best practice: prove the browser has the cookie by calling /me before navigating
      let meResponse;
      try {
        meResponse = await getMe(signal);
        
        // Check if request was aborted
        if (signal.aborted) {
          return; // Don't set state if aborted
        }
        
        if (shouldLog()) {
          console.log('[LOGIN] me-after-login status', { cid, status: 200 });
        }
      } catch (meErr) {
        // Check if error is due to abort (not a real error)
        if (meErr.name === 'AbortError' || signal.aborted) {
          return; // Don't set error state for aborted requests
        }
        
        if (shouldLog()) {
          console.log('[LOGIN] me-after-login status', { cid, status: meErr?.status ?? 'err' });
        }
        safeSetState(setError, 'Session could not be established. Please try again.');
        safeSetState(setLoading, false);
        return;
      }

      if (shouldLog()) {
        console.log('[LOGIN] navigate start', { cid });
      }
      onLoginSuccess(meResponse);
      navigate('/calendar', { replace: true });
      if (shouldLog()) {
        console.log('[LOGIN] navigate complete', { cid });
      }
    } catch (err) {
      // Check if error is due to abort (not a real error)
      if (err.name === 'AbortError' || signal.aborted) {
        return; // Don't set error state for aborted requests
      }
      
      if (shouldLog()) {
        console.log('[LOGIN] login response status/outcome', { cid, status: err?.status, outcome: err?.outcome });
      }
      if (err?.errorCode === 'SITE_ACCESS_DENIED' && err?.details) {
        safeSetState(setSiteAccessDenied, {
          message: err.message || getLoginErrorMessage(err),
          suggestedLoginUrl: err.details.suggestedLoginUrl || null,
        });
      }
      safeSetState(setError, getLoginErrorMessage(err));
      safeSetState(setLoading, false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    error,
    loading,
    handleSubmit,
    siteAccessDenied,
  };
}
